/**
 * ADO #230 — Record component → virtual resource helper.
 *
 * In Java, a record's components are implicit instance fields. Inside
 * instance methods and compact constructors they're accessible by bare
 * name (`x`, `y`) just like class fields would be. But the FlowBuilder
 * expression validator and variable picker operate on flow.resources +
 * globalResources; record components aren't in either list.
 *
 * This helper bridges the gap: for a flow that lives in a record file,
 * it returns the record's components as `_fromRecordComponent`-tagged
 * virtual resources the editors can add to their scope.
 *
 * Static flows inside a record cannot access instance components, so
 * the helper returns an empty array for those. Main, constructor, and
 * compactConstructor are not static; instance methods are not static.
 *
 * @param {Object} flow      the active flow (must carry fileName)
 * @param {Array}  files     the project's files (each with fileClassType + recordComponents)
 * @returns {Array}          virtual resources — empty unless flow is inside a record
 */
export function getRecordComponentResources(flow, files) {
  if (!flow || !files || files.length === 0) return [];

  // Static methods don't have access to instance components — skip.
  const ft = flow.flowType || (flow.isStatic === false ? 'instance' : 'static');
  if (ft === 'static') return [];

  const file = files.find(f => f.name === flow.fileName);
  if (!file || file.fileClassType !== 'record') return [];

  const components = file.recordComponents || [];
  return components.map(c => ({
    id: `res-record-component-${c.name}`,
    name: c.name,
    resourceType: 'variable',
    dataType: c.type,
    initialValue: '',
    scope: 'record-component',
    _fromRecordComponent: true,  // tag so editors can show a distinct pill/chip
  }));
}
